// src/pages/MarketplaceHomePage.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HeroVideoBanner from "../components/HeroVideoBanner";

/* =========================
   Données
========================= */
const SERVICE_CARDS = [
  {
    id: 1,
    title: "Choisissez parmi nos options de livraison",
    text: "Sous-traitez une partie de votre logistique à GoShop et profitez d’une solution adaptée aux vendeurs de toutes tailles.",
    linkLabel: "Découvrir nos solutions logistiques",
    imageUrl: "/images/services-logistique.jpg",
  },
  {
    id: 2,
    title: "Gagnez en visibilité auprès de milliers de clients",
    text: "Boostez vos ventes grâce à des bannières sponsorisées et mises en avant sur la page d’accueil GoShop.",
    linkLabel: "En savoir plus sur la publicité",
    imageUrl: "/images/services-publicite.jpg",
  },
  {
    id: 3,
    title: "Vendez facilement vers d’autres villes et pays",
    text: "Utilisez votre compte vendeur GoShop pour toucher des clients dans différentes villes et pays d’Afrique de l’Ouest.",
    linkLabel: "Voir les options d’expansion",
    imageUrl: "/images/services-expansion.jpg",
  },
];

const SUBSCRIPTIONS = [
  {
    id: "standard",
    name: "Pack Standard",
    price: "0 FCFA / mois*",
    subtitle: "Commission sur chaque vente",
    shortText: "Pour commencer sans abonnement mensuel.",
  },
  {
    id: "standard_plus",
    name: "Pack Standard+",
    price: "5 000 FCFA / mois*",
    subtitle: "Outils essentiels pour vendre mieux",
    shortText: "Pour les vendeurs qui veulent plus de contrôle.",
  },
  {
    id: "pro_semestre",
    name: "Pack Pro (Semestriel)",
    price: "10 000 FCFA / 6 mois*",
    subtitle: "Outils avancés, paiement semestriel",
    shortText: "Une formule souple avec les avantages Pro.",
  },
  {
    id: "pro",
    name: "Pack Pro (Annuel)",
    price: "15 000 FCFA / an*",
    subtitle: "Meilleure valeur + outils avancés",
    shortText: "Le pack le plus rentable et le plus complet.",
    featured: true,
  },
];

const PRICING_FEATURE_ROWS = [
  {
    id: "list-one",
    label: "Lister des produits un par un",
    values: { standard: "✓", standard_plus: "✓", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "static-prices",
    label: "Définir des prix statiques",
    values: { standard: "✓", standard_plus: "✓", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "fba",
    label: "Externaliser le stockage & la livraison (Fulfillment par GoShop)",
    values: {
      standard: "Option payante",
      standard_plus: "Option payante",
      pro_semestre: "✓",
      pro: "✓",
    },
  },
  {
    id: "merchant",
    label: "Livrer les commandes vous-même",
    values: { standard: "✓", standard_plus: "✓", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "bulk-ops",
    label: "Opérations en masse (listing / prix / stock)",
    values: { standard: "—", standard_plus: "✓", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "basic-stats",
    label: "Statistiques essentielles",
    values: { standard: "—", standard_plus: "✓", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "brand-tools",
    label: "Construire votre marque",
    values: { standard: "—", standard_plus: "✓", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "priority-support",
    label: "Support prioritaire",
    values: { standard: "—", standard_plus: "Option", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "auto-pricing",
    label: "Automatiser et optimiser les prix",
    values: { standard: "—", standard_plus: "—", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "ads",
    label: "Publicité avec GoShop Ads",
    values: { standard: "—", standard_plus: "Option", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "new-seller-guide",
    label: "Accompagnement New Seller Guide",
    values: { standard: "—", standard_plus: "—", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "b2b",
    label: "Vendre B2B",
    values: { standard: "—", standard_plus: "—", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "global",
    label: "Expansion multi-villes / pays",
    values: { standard: "—", standard_plus: "Option", pro_semestre: "✓", pro: "✓" },
  },
  {
    id: "api",
    label: "Vendre avec applications et API",
    values: { standard: "—", standard_plus: "—", pro_semestre: "✓", pro: "✓" },
  },
];

const QUICK_STEPS = [
  {
    id: 1,
    title: "Créez votre demande vendeur",
    text: "Remplissez le formulaire avec vos informations et les catégories que vous souhaitez vendre.",
  },
  {
    id: 2,
    title: "Validation de votre profil",
    text: "Notre équipe vérifie votre profil et vous oriente vers le pack adapté.",
  },
  {
    id: 3,
    title: "Activez votre abonnement",
    text: "Choisissez votre pack et payez pour débloquer toutes les fonctionnalités.",
  },
  {
    id: 4,
    title: "Ajoutez vos produits",
    text: "Publiez votre catalogue et commencez à recevoir des commandes rapidement.",
  },
];

const FAQ_ITEMS = [
  {
    q: "Y a-t-il des frais pour devenir vendeur ?",
    a: "L’inscription est gratuite. Ensuite, vous choisissez un pack (Standard, Standard+, Pro Semestriel ou Pro Annuel) et une commission peut s’appliquer selon votre contrat.",
  },
  {
    q: "Les packs sont-ils liés aux catégories de produits ?",
    a: "Oui. Chaque pack peut limiter ou étendre le nombre de catégories activables. Vous pouvez demander une évolution de votre offre si votre activité grandit.",
  },
  {
    q: "Puis-je vendre sans que le client crée un compte ?",
    a: "Oui. GoShop est pensé pour le contexte local : le client peut commander simplement, avec Mobile Money, paiement à la livraison ou autres options prises en charge.",
  },
  {
    q: "Quel pack choisir au départ ?",
    a: "Si vous voulez la meilleure valeur avec les outils avancés, le Pack Pro Annuel est le plus intéressant. Si vous préférez tester sur une période plus courte, choisissez le Pro Semestriel.",
  },
];

/* =========================
   Composants UI
========================= */
function SectionHeader({ eyebrow, title, text, center = false }) {
  return (
    <div className={`mk-section-head ${center ? "center" : ""}`}>
      {eyebrow && <p className="mk-section-eyebrow">{eyebrow}</p>}
      <h2 className="mk-section-title">{title}</h2>
      {text && <p className="mk-section-text">{text}</p>}
    </div>
  );
}

function StatCard({ value, text }) {
  return (
    <div className="mk-stat-card">
      <div className="mk-stat-value">{value}</div>
      <p>{text}</p>
    </div>
  );
}

function PlanMiniCard({ plan }) {
  return (
    <article className={`mk-plan-mini ${plan.featured ? "featured" : ""}`}>
      {plan.featured && <div className="mk-plan-mini-badge">Recommandé</div>}
      <h3>{plan.name}</h3>
      <div className="mk-plan-mini-price">{plan.price}</div>
      <p className="mk-plan-mini-sub">{plan.subtitle}</p>
      <p className="mk-plan-mini-text">{plan.shortText}</p>
      <Link
        to={`/inscription-vendeur?pack=${plan.id}`}
        className={`mk-btn mk-btn-wide ${
          plan.featured ? "mk-btn-pro" : "mk-btn-outline"
        }`}
      >
        {plan.id === "pro"
          ? "Choisir Pro Annuel"
          : plan.id === "pro_semestre"
          ? "Choisir Pro 6 mois"
          : plan.id === "standard_plus"
          ? "Choisir Standard+"
          : "Choisir Standard"}
      </Link>
    </article>
  );
}

/* =========================
   Page
========================= */
export default function MarketplaceHomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const year = useMemo(() => new Date().getFullYear(), []);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navItems = [
    { id: "pourquoi", label: "Pourquoi GoShop" },
    { id: "parcours", label: "Comment ça marche" },
    { id: "services", label: "Services" },
    { id: "abonnements", label: "Abonnements" },
    { id: "faq", label: "FAQ" },
  ];

  return (
    <div className="mk-page">
      {/* Header */}
      <header className="mk-header">
        <div className="mk-header-inner">
          <div className="mk-logo-block">
            <div className="mk-logo-icon">G</div>
            <div className="mk-logo-text">
              <span className="mk-logo-title">GoShop Marketplace</span>
              <span className="mk-logo-sub">Plateforme vendeurs · Afrique de l’Ouest</span>
            </div>
          </div>

          <button
            type="button"
            className="mk-burger"
            onClick={() => setMobileMenuOpen((p) => !p)}
            aria-label="Ouvrir le menu"
          >
            <span />
            <span />
            <span />
          </button>

          <nav className={`mk-nav ${mobileMenuOpen ? "is-open" : ""}`}>
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  scrollToId(item.id);
                  setMobileMenuOpen(false);
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="mk-actions">
            <Link to="/connexion-vendeur" className="mk-link-login">
              Connexion vendeur
            </Link>

            <Link to="/inscription-vendeur?pack=pro" className="mk-btn mk-btn-primary">
              Devenir vendeur
            </Link>
          </div>
        </div>
      </header>

      {/* Main */}
      <div className="mk-container">
        <main className="mk-main">
          <div className="mk-kicker">
            <span className="mk-kicker-pill">Inscris-toi en tant que vendeur</span>
          </div>

          <HeroVideoBanner />

          {/* HERO */}
          <section className="mk-hero-template">
            <div className="mk-hero-left">
              <div className="mk-card mk-hero-card">
                <p className="mk-eyebrow">Plateforme vendeurs</p>

                <h1 className="mk-title">
                  Développez votre business avec <span className="mk-accent">GoShop</span>
                </h1>

                <p className="mk-subtitle">
                  Rejoignez la marketplace GoShop et vendez vos produits à des milliers de clients
                  en Afrique de l’Ouest. Paiement Mobile Money, paiement à la livraison, sans que
                  le client ait besoin de créer un compte.
                </p>

                <ul className="mk-bullets">
                  <li>
                    <span className="mk-dot mk-dot-indigo">●</span>
                    <span>
                      <strong>Produits variés :</strong> électronique, beauté, maison,
                      alimentaire, automobile, etc.
                    </span>
                  </li>
                  <li>
                    <span className="mk-dot mk-dot-amber">●</span>
                    <span>
                      <strong>Adapté à la réalité locale :</strong> Mobile Money, livraison,
                      point relais, paiement à la réception.
                    </span>
                  </li>
                  <li>
                    <span className="mk-dot mk-dot-green">●</span>
                    <span>
                      <strong>Parcours simple :</strong> inscription, validation, paiement du
                      pack, accès au dashboard vendeur.
                    </span>
                  </li>
                </ul>

                <div className="mk-cta-row">
                  <Link to="/inscription-vendeur?pack=pro" className="mk-btn mk-btn-pro">
                    🚀 Commencer avec le Pack Pro
                  </Link>

                  <button
                    type="button"
                    className="mk-btn mk-btn-ghost"
                    onClick={() => scrollToId("abonnements")}
                  >
                    Comparer les packs
                  </button>
                </div>

                <div className="mk-mini-note">
                  <strong>Pack Pro Annuel</strong> = meilleure valeur :{" "}
                  <strong>15 000 FCFA/an</strong> soit <strong>≈ 1 250 FCFA/mois</strong>.{" "}
                  <br />
                  Nouveau : <strong>Pack Pro Semestriel</strong> :{" "}
                  <strong>10 000 FCFA / 6 mois</strong>.
                </div>
              </div>
            </div>

            <aside className="mk-hero-right">
              <div className="mk-card mk-quick-start-card">
                <div className="mk-quick-start-top">
                  <div className="mk-quick-pill">Démarrage rapide</div>
                  <h3>Devenir vendeur en 4 étapes</h3>
                </div>

                <div className="mk-quick-steps">
                  {QUICK_STEPS.map((step) => (
                    <div key={step.id} className="mk-quick-step">
                      <div className="mk-quick-step-num">{step.id}</div>
                      <div className="mk-quick-step-body">
                        <strong>{step.title}</strong>
                        <p>{step.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Link to="/inscription-vendeur?pack=pro" className="mk-btn mk-btn-primary mk-btn-wide">
                  Créer ma demande vendeur
                </Link>
              </div>
            </aside>
          </section>

          {/* Opportunités */}
          <section className="mk-op-section">
            <div className="mk-op-inner">
              <div className="mk-op-image">
                <img src="/images/opportunites-goshop.png" alt="Vendeuse GoShop utilisant une tablette" />
              </div>

              <div className="mk-op-content">
                <SectionHeader
                  eyebrow="Opportunités"
                  title="Profitez de tout le potentiel de GoShop"
                  text="Des outils concrets pour vendre mieux, toucher plus de clients et structurer votre activité."
                />

                <div className="mk-op-metrics">
                  <StatCard
                    value="10k+"
                    text="Clients et visiteurs touchés chaque mois via GoShop et nos vitrines partenaires.*"
                  />
                  <StatCard
                    value="45%"
                    text="de croissance moyenne observée chez les vendeurs actifs sur plusieurs mois."
                  />
                  <StatCard
                    value="ZERO"
                    text="frais mensuels ou de configuration— vous payez que pour ce que vous vendez avec nos tarifs de recommandation compétitifs"
                  />
                  <StatCard
                    value="4+"
                    text="villes et pays cibles en Afrique de l’Ouest, avec extension progressive."
                  />
                </div>

                <p className="mk-op-note">
                  *Chiffres indicatifs basés sur notre activité actuelle et projections internes.
                </p>
              </div>
            </div>
          </section>

          {/* Pourquoi */}
          <section id="pourquoi" className="mk-gradient">
            <SectionHeader
              eyebrow="Pourquoi GoShop"
              title="GoShop : votre partenaire e-commerce"
              text="Une seule plateforme pour gérer votre catalogue, vos commandes, vos paiements et la relation client, avec un système d’abonnement adapté à votre activité."
            />

            <div className="mk-gradient-list">
              <div className="mk-gradient-item">✔ Interface simple pour ajouter vos produits.</div>
              <div className="mk-gradient-item">✔ Packs basés sur les catégories que vous souhaitez vendre.</div>
              <div className="mk-gradient-item">✔ Support en français et en langues locales.</div>
              <div className="mk-gradient-item">✔ Paiements adaptés au marché local.</div>
            </div>
          </section>

          {/* Commerçant physique */}
          <section className="mk-card mk-physical-card">
            <SectionHeader
              eyebrow="Boutique physique"
              title="Déjà commerçant physique ?"
              text="Transformez votre boutique locale en vitrine en ligne. Vendez à vos clients habituels et à de nouveaux clients en quelques clics."
            />
            <p className="mk-muted" style={{ margin: 0 }}>
              <strong>Idéal pour :</strong> boutiques de vêtements, shops électroniques,
              supermarchés, épiceries, salons, accessoires, équipements maison, etc.
            </p>
          </section>

          {/* Parcours */}
          <section id="parcours">
            <SectionHeader
              eyebrow="Comment ça marche"
              title="Parcours vendeur GoShop : de la demande à l’abonnement"
              text="Un parcours simple et structuré pour vous permettre de démarrer rapidement."
            />

            <div className="mk-step-grid">
              {[
                {
                  title: "1. Demande de devenir vendeur",
                  text: "Vous remplissez le formulaire 'Devenir vendeur' avec vos informations et les catégories que vous souhaitez vendre.",
                },
                {
                  title: "2. Email de confirmation",
                  text: "Vous recevez un email indiquant que votre demande est bien reçue et en attente de validation par un administrateur.",
                },
                {
                  title: "3. Validation par l’admin & choix du pack",
                  text: "L’admin valide votre profil, vous attribue un pack selon vos catégories et vous envoie un lien pour définir votre mot de passe.",
                },
                {
                  title: "4. Première connexion & paiement du pack",
                  text: "À la première connexion, si aucun abonnement actif n’est payé, vous êtes redirigé vers la page de paiement de votre pack.",
                },
                {
                  title: "5. Accès complet à l’espace vendeur",
                  text: "Une fois le pack actif : tableau de bord vendeur (produits, commandes, paiements, statistiques selon pack).",
                },
              ].map((step, idx) => (
                <div key={step.title} className="mk-card mk-step-card">
                  <div className="mk-step-number">{idx + 1}</div>
                  <strong>{step.title}</strong>
                  <div className="mk-muted">{step.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Offres */}
          <section id="offres">
            <SectionHeader
              eyebrow="Avantages"
              title="Ce que GoShop offre aux vendeurs"
              text="Des outils utiles au quotidien pour vendre, communiquer et développer votre activité."
            />

            <div className="mk-grid">
              {[
                "Mise en avant de vos produits sur la page d’accueil.",
                "Support pour configurer votre catalogue et vos prix.",
                "Gestion multi-catégories (alimentaire, électronique, mode, véhicules…).",
                "Statistiques détaillées (ventes, clics, meilleures références) selon pack.",
                "Outils pour partager vos produits par WhatsApp et réseaux sociaux.",
                "Accompagnement pour optimiser vos fiches produits (photos, titres, descriptions).",
              ].map((text, idx) => (
                <div key={idx} className="mk-card mk-offer">
                  {text}
                </div>
              ))}
            </div>
          </section>

          {/* Services */}
          <section id="services">
            <SectionHeader
              eyebrow="Services"
              title="Des solutions pour développer votre activité"
              text="GoShop ne se limite pas à la vente : nous vous aidons aussi sur la logistique, la publicité et l’expansion."
            />

            <div className="mk-services">
              {SERVICE_CARDS.map((card) => (
                <article key={card.id} className="mk-service-card">
                  <div className="mk-service-media">
                    <img src={card.imageUrl} alt={card.title} />
                  </div>

                  <div className="mk-service-body">
                    <h3>{card.title}</h3>
                    <p>{card.text}</p>

                    <button type="button" className="mk-service-link">
                      {card.linkLabel} →
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* Mini cards packs */}
          <section>
            <SectionHeader
              eyebrow="Choisissez votre formule"
              title="Des packs adaptés à chaque profil vendeur"
              text="Comparez rapidement les principales offres avant de consulter le tableau détaillé."
              center
            />

            <div className="mk-plan-mini-grid">
              {SUBSCRIPTIONS.map((plan) => (
                <PlanMiniCard key={plan.id} plan={plan} />
              ))}
            </div>
          </section>

          {/* Abonnements */}
          <section id="abonnements" className="mk-section-full">
            <div className="mk-section-inner">
              <SectionHeader
                eyebrow="Abonnements"
                title="Comparez les abonnements vendeurs GoShop"
                text="Choisissez votre pack : Standard, Standard+, Pro Semestriel ou Pro Annuel. Le Pack Pro Annuel reste le plus avantageux."
              />

              <div className="mk-pro-promo">
                <div className="mk-pro-promo-left">
                  <div className="mk-badge">Pack Pro recommandé</div>
                  <h3>Le Pro Annuel est la meilleure offre</h3>
                  <p className="mk-muted" style={{ marginBottom: 0 }}>
                    Le Pro vous donne tous les outils avancés. Le plan annuel est le plus rentable :{" "}
                    <strong>15 000 FCFA/an</strong> (≈ <strong>1 250 FCFA/mois</strong>). Si vous préférez payer en 2 fois,
                    choisissez le <strong>Pro Semestriel</strong> : <strong>10 000 FCFA / 6 mois</strong>.
                  </p>
                </div>

                <div className="mk-pro-promo-right">
                  <div className="mk-pro-price">15 000 FCFA / an</div>
                  <div className="mk-pro-sub">≈ 1 250 FCFA / mois</div>
                  <Link to="/inscription-vendeur?pack=pro" className="mk-btn mk-btn-pro mk-btn-wide">
                    🚀 Choisir Pro Annuel
                  </Link>

                  <Link to="/inscription-vendeur?pack=pro_semestre" className="mk-btn mk-btn-outline mk-btn-wide">
                    Choisir Pro Semestriel
                  </Link>
                </div>
              </div>

              <div className="mk-sellplans-card">
                <div className="mk-sellplans-table-wrapper">
                  <table className="mk-sellplans-table">
                    <thead>
                      <tr>
                        <th className="mk-sellplans-col-feature">Fonctionnalités</th>
                        {SUBSCRIPTIONS.map((plan) => (
                          <th
                            key={plan.id}
                            className={
                              "mk-sellplans-col-plan " + (plan.featured ? "mk-plan-featured" : "")
                            }
                          >
                            {plan.featured && <div className="mk-plan-badge">Recommandé</div>}
                            <div className="mk-sellplans-plan-name">{plan.name}</div>
                            <div className="mk-sellplans-plan-price">{plan.price}</div>
                            <div className="mk-sellplans-plan-subtitle">{plan.subtitle}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {PRICING_FEATURE_ROWS.map((row, rowIndex) => (
                        <tr key={row.id} className={rowIndex % 2 === 1 ? "mk-sellplans-row-alt" : ""}>
                          <th className="mk-sellplans-feature-cell">{row.label}</th>

                          {SUBSCRIPTIONS.map((plan) => {
                            const value = row.values[plan.id] ?? "";
                            const isCheck = value === "✓";
                            const isDash = value === "—";
                            return (
                              <td
                                key={plan.id + row.id}
                                className={
                                  "mk-sellplans-value-cell " + (plan.featured ? "mk-cell-featured" : "")
                                }
                              >
                                {isCheck && <span className="mk-sellplans-check">✓</span>}
                                {!isCheck && isDash && <span className="mk-dash">—</span>}
                                {!isCheck && !isDash && value}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>

                    <tfoot>
                      <tr>
                        <th></th>
                        {SUBSCRIPTIONS.map((plan) => (
                          <td
                            key={plan.id + "-cta"}
                            className={"mk-sellplans-cta-cell " + (plan.featured ? "mk-cell-featured" : "")}
                          >
                            <Link
                              to={`/inscription-vendeur?pack=${plan.id}`}
                              className={
                                "mk-btn mk-btn-wide " +
                                (plan.id === "pro"
                                  ? "mk-btn-pro"
                                  : plan.id === "pro_semestre"
                                  ? "mk-btn-outline"
                                  : plan.id === "standard_plus"
                                  ? "mk-btn-outline"
                                  : "mk-btn-ghost")
                              }
                            >
                              {plan.id === "standard"
                                ? "Choisir Standard"
                                : plan.id === "standard_plus"
                                ? "Choisir Standard+"
                                : plan.id === "pro_semestre"
                                ? "Choisir Pro 6 mois"
                                : "Choisir Pro Annuel"}
                            </Link>

                            {plan.id === "pro" && <div className="mk-cta-hint">Meilleure valeur (annuel)</div>}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <p className="mk-sellplans-note">
                  *Standard : 0 FCFA/mois + commission. Standard+ : 5 000 FCFA/mois + outils essentiels.
                  Pro Semestriel : 10 000 FCFA / 6 mois. Pro Annuel : 15 000 FCFA/an (≈ 1 250 FCFA/mois)
                  + outils avancés.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq">
            <SectionHeader
              eyebrow="FAQ"
              title="Questions fréquentes"
              text="Les réponses aux principales questions que les vendeurs se posent avant de rejoindre GoShop."
            />

            <div className="mk-grid">
              {FAQ_ITEMS.map((item) => (
                <div key={item.q} className="mk-card mk-faq">
                  <p className="mk-faq-q">
                    <strong>{item.q}</strong>
                  </p>
                  <p className="mk-muted" style={{ margin: 0 }}>
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA final */}
          <section className="mk-final-banner">
            <div className="mk-final-banner-inner">
              <div>
                <p className="mk-final-eyebrow">Prêt à démarrer ?</p>
                <h2>Rejoignez GoShop et commencez à vendre rapidement</h2>
                <p>
                  Choisissez le pack qui correspond à votre ambition et activez votre espace vendeur.
                </p>
              </div>

              <div className="mk-final-actions">
                <Link to="/inscription-vendeur?pack=pro" className="mk-btn mk-btn-pro mk-btn-wide">
                  🚀 Pack Pro Annuel
                </Link>
                <Link to="/inscription-vendeur?pack=pro_semestre" className="mk-btn mk-btn-outline mk-btn-wide">
                  Pro 6 mois
                </Link>
                <Link to="/inscription-vendeur?pack=standard_plus" className="mk-btn mk-btn-outline mk-btn-wide">
                  Standard+
                </Link>
                <Link to="/inscription-vendeur?pack=standard" className="mk-btn mk-btn-ghost mk-btn-wide">
                  Standard
                </Link>
              </div>
            </div>
          </section>

          <footer className="mk-footer">
            © {year} GoShop Marketplace – Plateforme vendeurs, Afrique de l’Ouest.
          </footer>
        </main>
      </div>

      <style>{`
        .mk-page{
          min-height:100vh;
          background:#ffffff;
          font-family:'Bogle', Helvetica Neue, Helvetica, Arial, sans-serif;
          color:#0f172a;
        }

        .mk-container{
          padding:24px 16px 44px;
          display:flex;
          justify-content:center;
        }

        .mk-main{
          width:100%;
          max-width:1120px;
          display:flex;
          flex-direction:column;
          gap:26px;
          font-size:15px;
          line-height:1.6;
        }

        /* Header */
        .mk-header{
          position:sticky; top:0; z-index:50;
          background:rgba(255,255,255,.92);
          border-bottom:1px solid #e5e7eb;
          backdrop-filter:blur(10px);
        }

        .mk-header-inner{
          max-width:1120px;
          margin:0 auto;
          padding:10px 16px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:16px;
        }

        .mk-logo-block{display:flex; align-items:center; gap:10px; min-width:0;}
        .mk-logo-icon{
          width:36px;height:36px;border-radius:12px;
          background:linear-gradient(135deg,#16a34a 0%,#22c55e 40%,#4ade80 100%);
          display:flex;align-items:center;justify-content:center;
          color:#fff;font-weight:900;font-size:18px;
          flex-shrink:0;
        }
        .mk-logo-text{display:flex;flex-direction:column;line-height:1.2;}
        .mk-logo-title{font-size:15px;font-weight:900;color:#111827;}
        .mk-logo-sub{font-size:11px;color:#6b7280;}

        .mk-nav{
          flex:1;
          display:flex;
          justify-content:center;
          gap:18px;
          font-size:14px;
        }
        .mk-nav button{
          border:none;
          background:transparent;
          padding:0;
          cursor:pointer;
          color:#4b5563;
          font-size:14px;
          font-weight:600;
        }
        .mk-nav button:hover{color:#111827;}

        .mk-actions{display:flex; align-items:center; gap:10px;}
        .mk-link-login{
          font-size:13px;
          color:#4b5563;
          text-decoration:none;
          white-space:nowrap;
          font-weight:700;
        }
        .mk-link-login:hover{color:#111827;}

        .mk-burger{
          display:none;
          flex-direction:column;
          justify-content:center;
          align-items:center;
          width:34px;height:34px;border-radius:999px;
          border:1px solid #e5e7eb;background:#fff;
          padding:0;gap:4px;cursor:pointer;
        }
        .mk-burger span{
          width:16px;height:2px;border-radius:999px;background:#111827;
        }

        @media (max-width:768px){
          .mk-header-inner{flex-wrap:wrap;}
          .mk-actions{
            order:3;
            width:100%;
            justify-content:flex-start;
            margin-top:8px;
            gap:8px;
            flex-wrap:wrap;
          }
          .mk-burger{display:inline-flex;order:2;}
          .mk-nav{
            order:4;
            width:100%;
            display:none;
            flex-direction:column;
            align-items:flex-start;
            gap:8px;
            margin-top:8px;
            padding-top:8px;
            border-top:1px solid #f1f5f9;
          }
          .mk-nav.is-open{display:flex;}
        }

        /* Buttons */
        .mk-btn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding:10px 16px;
          border-radius:999px;
          font-size:14px;
          font-weight:800;
          text-decoration:none;
          border:1px solid transparent;
          cursor:pointer;
          transition:transform .15s ease, box-shadow .2s ease, background .2s ease, color .2s ease, border-color .2s ease;
          white-space:nowrap;
        }

        .mk-btn-primary{
          background:#1e17eb;
          color:#fff;
          box-shadow:0 10px 22px rgba(30,23,235,0.28);
        }
        .mk-btn-primary:hover{
          background:#0b0a0f;
          color:#e0e7ff;
          box-shadow:0 14px 30px rgba(15,23,42,0.35);
          transform:translateY(-1px);
        }

        .mk-btn-ghost{
          background:#fff;
          color:#111827;
          border:1px solid #e5e7eb;
        }
        .mk-btn-ghost:hover{
          background:#f3f4f6;
          transform:translateY(-1px);
        }

        .mk-btn-outline{
          background:#fff;
          color:#111827;
          border:1px solid #d1d5db;
        }
        .mk-btn-outline:hover{
          background:#f3f4f6;
          transform:translateY(-1px);
        }

        .mk-btn-pro{
          background:linear-gradient(135deg,#f97316 0%, #fb923c 55%, #f97316 100%);
          color:#111827;
          border:1px solid rgba(0,0,0,0.06);
          box-shadow:0 12px 28px rgba(249,115,22,0.25);
        }
        .mk-btn-pro:hover{
          background:#ea580c;
          color:#fff;
          transform:translateY(-1px);
          box-shadow:0 16px 34px rgba(234,88,12,0.30);
        }

        .mk-btn-wide{width:100%; max-width:320px;}

        /* Kicker */
        .mk-kicker{margin-bottom:2px;}
        .mk-kicker-pill{
          display:inline-block;
          font-size:12px;
          color:#5b21b6;
          font-weight:900;
          letter-spacing:.14em;
          text-transform:uppercase;
        }

        /* Section headers */
        .mk-section-head{margin-bottom:14px;}
        .mk-section-head.center{text-align:center;}
        .mk-section-eyebrow{
          margin:0 0 6px;
          font-size:12px;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.16em;
          color:#6d28d9;
        }
        .mk-section-title{
          margin:0;
          font-size:26px;
          line-height:1.18;
          font-weight:900;
          color:#111827;
        }
        .mk-section-text{
          margin:8px 0 0;
          color:#4b5563;
          max-width:760px;
        }
        .mk-section-head.center .mk-section-text{
          margin-left:auto;
          margin-right:auto;
        }

        /* Card */
        .mk-card{
          background:#fff;
          border-radius:24px;
          padding:18px 18px 20px;
          border:1px solid #e5e7eb;
          box-shadow:0 20px 60px rgba(15,23,42,0.10);
        }
        .mk-muted{color:#4b5563;}

        /* Hero template */
        .mk-hero-template{
          display:grid;
          grid-template-columns: minmax(0, 1.3fr) minmax(320px, .7fr);
          gap:18px;
          align-items:start;
        }

        .mk-hero-card{
          padding:24px 24px 26px;
          height:100%;
        }

        .mk-eyebrow{
          font-size:13px;
          text-transform:uppercase;
          letter-spacing:.18em;
          color:#9ca3af;
          font-weight:800;
          margin:0 0 8px;
        }

        .mk-title{
          font-size:38px;
          line-height:1.12;
          font-weight:900;
          margin:0 0 10px;
          color:#0f172a;
        }

        .mk-accent{color:#16a34a;}
        .mk-subtitle{max-width:700px; margin:0 0 16px;}

        .mk-bullets{
          list-style:none;
          padding:0;
          margin:0 0 14px;
          display:grid;
          gap:8px;
          color:#111827;
        }

        .mk-bullets li{
          display:flex;
          gap:8px;
          align-items:flex-start;
        }

        .mk-dot{font-weight:900; line-height:1.2;}
        .mk-dot-indigo{color:#4f46e5;}
        .mk-dot-amber{color:#f59e0b;}
        .mk-dot-green{color:#16a34a;}

        .mk-cta-row{
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          align-items:center;
          margin-top:8px;
        }

        .mk-mini-note{
          margin-top:12px;
          font-size:12px;
          color:#6b7280;
          background:#f9fafb;
          border:1px solid #e5e7eb;
          padding:10px 12px;
          border-radius:14px;
          max-width:780px;
        }

        .mk-quick-start-card{
          position:sticky;
          top:84px;
          padding:20px 18px;
        }

        .mk-quick-pill{
          display:inline-flex;
          padding:6px 10px;
          border-radius:999px;
          background:#eff6ff;
          color:#1d4ed8;
          font-size:12px;
          font-weight:900;
        }

        .mk-quick-start-top h3{
          margin:10px 0 0;
          font-size:20px;
          font-weight:900;
          color:#111827;
        }

        .mk-quick-steps{
          display:flex;
          flex-direction:column;
          gap:12px;
          margin:16px 0 18px;
        }

        .mk-quick-step{
          display:flex;
          gap:12px;
          align-items:flex-start;
        }

        .mk-quick-step-num{
          width:30px;
          height:30px;
          border-radius:999px;
          background:#111827;
          color:#fff;
          font-size:13px;
          font-weight:900;
          display:flex;
          align-items:center;
          justify-content:center;
          flex-shrink:0;
        }

        .mk-quick-step-body strong{
          display:block;
          color:#111827;
          margin-bottom:2px;
          font-size:14px;
        }

        .mk-quick-step-body p{
          margin:0;
          color:#6b7280;
          font-size:13px;
          line-height:1.5;
        }

        @media (max-width:992px){
          .mk-hero-template{
            grid-template-columns: 1fr;
          }
          .mk-quick-start-card{
            position:relative;
            top:0;
          }
        }

        @media (max-width:768px){
          .mk-title{font-size:30px;}
        }

        /* Opportunities */
        .mk-op-section{
          background:#eff6ff;
          border-radius:24px;
          padding:24px 24px 26px;
          border:1px solid rgba(29,78,216,0.10);
        }

        .mk-op-inner{
          display:flex;
          align-items:center;
          gap:24px;
        }

        .mk-op-image img{
          width:100%;
          max-width:260px;
          height:auto;
          display:block;
          border-radius:999px;
        }

        .mk-op-content{flex:1;}
        .mk-op-metrics{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:16px;
        }

        .mk-stat-card{
          border-left:4px solid #fbbf24;
          padding-left:12px;
        }

        .mk-stat-value{
          font-size:28px;
          font-weight:900;
          color:#1d4ed8;
          margin-bottom:4px;
        }

        .mk-stat-card p{
          margin:0;
          font-size:14px;
          color:#4b5563;
        }

        .mk-op-note{
          margin-top:10px;
          font-size:12px;
          color:#6b7280;
        }

        @media (max-width:768px){
          .mk-op-inner{flex-direction:column;align-items:flex-start;}
          .mk-op-image img{max-width:220px;}
          .mk-op-metrics{grid-template-columns:1fr;}
        }

        /* Gradient block */
        .mk-gradient{
          border-radius:24px;
          padding:22px;
          background:linear-gradient(135deg,#0f172a 0%,#0f766e 45%,#16a34a 100%);
          color:#e5e7eb;
        }

        .mk-gradient .mk-section-title{color:#fff;}
        .mk-gradient .mk-section-text{color:#d1fae5;}

        .mk-gradient-list{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:10px;
          margin-top:14px;
        }

        .mk-gradient-item{
          background:rgba(255,255,255,.10);
          border:1px solid rgba(255,255,255,.14);
          border-radius:16px;
          padding:12px 14px;
          font-size:14px;
        }

        @media (max-width:768px){
          .mk-gradient-list{grid-template-columns:1fr;}
        }

        /* Physical card */
        .mk-physical-card{padding:22px;}

        /* Generic grids */
        .mk-grid{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:12px;
        }

        @media (max-width:768px){
          .mk-grid{grid-template-columns:1fr;}
        }

        .mk-step-grid{
          display:grid;
          grid-template-columns:repeat(3,minmax(0,1fr));
          gap:12px;
        }

        @media (max-width:992px){
          .mk-step-grid{grid-template-columns:repeat(2,minmax(0,1fr));}
        }

        @media (max-width:768px){
          .mk-step-grid{grid-template-columns:1fr;}
        }

        .mk-step-card{
          position:relative;
          padding-top:46px;
        }

        .mk-step-number{
          position:absolute;
          top:16px;
          left:16px;
          width:28px;
          height:28px;
          border-radius:999px;
          background:#16a34a;
          color:#fff;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:900;
          font-size:13px;
        }

        .mk-offer{
          border-radius:18px;
          box-shadow:none;
          font-weight:600;
        }

        /* Services */
        .mk-services{
          display:grid;
          grid-template-columns:repeat(3,minmax(0,1fr));
          gap:16px;
        }

        @media (max-width:992px){
          .mk-services{grid-template-columns:repeat(2,minmax(0,1fr));}
        }

        @media (max-width:768px){
          .mk-services{grid-template-columns:1fr;}
        }

        .mk-service-card{
          background:#fff;
          border-radius:20px;
          overflow:hidden;
          border:1px solid #e5e7eb;
          box-shadow:0 12px 30px rgba(15,23,42,0.10);
          display:flex;
          flex-direction:column;
        }

        .mk-service-media{
          height:200px;
          background:#e5e7eb;
          overflow:hidden;
        }

        .mk-service-media img{
          width:100%;
          height:100%;
          object-fit:cover;
          display:block;
        }

        .mk-service-body{
          padding:14px 16px 16px;
          display:flex;
          flex-direction:column;
          gap:8px;
          flex:1;
        }

        .mk-service-body h3{
          font-size:16px;
          font-weight:900;
          color:#1d4ed8;
          margin:0;
          line-height:1.3;
        }

        .mk-service-body p{
          font-size:14px;
          color:#4b5563;
          margin:0;
          flex:1;
        }

        .mk-service-link{
          background:transparent;
          border:none;
          padding:0;
          margin-top:4px;
          font-size:14px;
          font-weight:800;
          color:#1d4ed8;
          cursor:pointer;
          align-self:flex-start;
        }

        /* Mini plans */
        .mk-plan-mini-grid{
          display:grid;
          grid-template-columns:repeat(4,minmax(0,1fr));
          gap:14px;
        }

        @media (max-width:1100px){
          .mk-plan-mini-grid{grid-template-columns:repeat(2,minmax(0,1fr));}
        }

        @media (max-width:768px){
          .mk-plan-mini-grid{grid-template-columns:1fr;}
        }

        .mk-plan-mini{
          position:relative;
          background:#fff;
          border-radius:22px;
          border:1px solid #e5e7eb;
          box-shadow:0 16px 36px rgba(15,23,42,0.08);
          padding:20px 18px;
          display:flex;
          flex-direction:column;
          gap:8px;
        }

        .mk-plan-mini.featured{
          border:1px solid rgba(249,115,22,0.30);
          box-shadow:0 18px 40px rgba(249,115,22,0.12);
          background:linear-gradient(180deg, #fff7ed 0%, #ffffff 40%);
        }

        .mk-plan-mini-badge{
          position:absolute;
          top:14px;
          right:14px;
          background:#ffedd5;
          color:#9a3412;
          border:1px solid #fed7aa;
          padding:5px 10px;
          border-radius:999px;
          font-size:11px;
          font-weight:900;
        }

        .mk-plan-mini h3{
          margin:0;
          font-size:18px;
          font-weight:900;
          color:#111827;
          padding-right:90px;
        }

        .mk-plan-mini-price{
          font-size:20px;
          font-weight:900;
          color:#16a34a;
        }

        .mk-plan-mini-sub{
          margin:0;
          color:#111827;
          font-weight:700;
          font-size:13px;
        }

        .mk-plan-mini-text{
          margin:0 0 8px;
          color:#6b7280;
          font-size:13px;
        }

        /* Full width section */
        .mk-section-full{
          position:relative;
          left:50%;
          right:50%;
          margin-left:-50vw;
          margin-right:-50vw;
          width:100vw;
        }

        .mk-section-inner{
          max-width:1120px;
          margin:0 auto;
          padding:24px 16px;
        }

        @media (max-width:768px){
          .mk-section-inner{padding:16px 12px;}
        }

        /* Pro promo */
        .mk-pro-promo{
          display:flex;
          gap:16px;
          align-items:stretch;
          padding:16px;
          border-radius:22px;
          border:1px solid rgba(249,115,22,0.25);
          background:linear-gradient(135deg, rgba(255,237,213,1) 0%, rgba(255,255,255,1) 55%);
          box-shadow:0 16px 40px rgba(249,115,22,0.10);
          margin:12px 0 14px;
        }

        .mk-pro-promo-left{flex:1; min-width:0;}

        .mk-pro-promo-right{
          width:320px;
          background:#fff;
          border-radius:18px;
          border:1px solid #fde68a;
          padding:14px;
          display:flex;
          flex-direction:column;
          justify-content:center;
          gap:8px;
          box-shadow:0 10px 24px rgba(15,23,42,0.06);
        }

        .mk-pro-promo h3{
          margin:8px 0 6px;
          font-size:18px;
          font-weight:900;
        }

        .mk-badge{
          display:inline-flex;
          align-items:center;
          gap:8px;
          font-size:12px;
          font-weight:900;
          color:#9a3412;
          background:#ffedd5;
          border:1px solid #fed7aa;
          padding:6px 10px;
          border-radius:999px;
          width:fit-content;
        }

        .mk-pro-price{
          font-size:18px;
          font-weight:900;
          color:#111827;
        }

        .mk-pro-sub{
          font-size:12px;
          color:#6b7280;
        }

        @media (max-width:768px){
          .mk-pro-promo{flex-direction:column;}
          .mk-pro-promo-right{width:100%;}
        }

        /* Selling plans table */
        .mk-sellplans-card{
          background:#fff;
          border-radius:24px;
          padding:18px 16px 18px;
          box-shadow:0 20px 45px rgba(15,23,42,0.10);
          border:1px solid #e5e7eb;
        }

        .mk-sellplans-table-wrapper{
          width:100%;
          overflow-x:auto;
        }

        .mk-sellplans-table{
          width:100%;
          min-width:900px;
          border-collapse:collapse;
          font-size:13px;
        }

        .mk-sellplans-table thead th{
          padding:12px 12px;
          border-bottom:2px solid #e5e7eb;
          vertical-align:bottom;
          background:#fff;
        }

        .mk-sellplans-col-feature{
          width:45%;
          text-align:left;
          font-weight:700;
          color:#6b7280;
        }

        .mk-sellplans-col-plan{
          text-align:center;
          position:relative;
        }

        .mk-plan-featured{
          background:linear-gradient(180deg, rgba(255,237,213,0.7) 0%, rgba(255,255,255,1) 70%);
          border-left:1px solid rgba(249,115,22,0.18);
          border-right:1px solid rgba(249,115,22,0.18);
        }

        .mk-plan-badge{
          position:absolute;
          top:10px;
          left:50%;
          transform:translateX(-50%);
          font-size:11px;
          font-weight:900;
          color:#9a3412;
          background:#ffedd5;
          border:1px solid #fed7aa;
          padding:4px 10px;
          border-radius:999px;
          white-space:nowrap;
        }

        .mk-sellplans-plan-name{
          font-size:14px;
          font-weight:900;
          color:#111827;
          margin-top:18px;
        }

        .mk-sellplans-plan-price{
          font-size:14px;
          font-weight:900;
          color:#111827;
          margin-top:2px;
        }

        .mk-sellplans-plan-subtitle{
          font-size:11px;
          color:#6b7280;
          margin-top:2px;
        }

        .mk-sellplans-table tbody th,
        .mk-sellplans-table tbody td{
          padding:9px 12px;
          border-bottom:1px solid #f3f4f6;
          vertical-align:middle;
        }

        .mk-sellplans-row-alt{background:#f9fafb;}
        .mk-sellplans-feature-cell{
          text-align:left;
          font-weight:500;
          color:#111827;
        }

        .mk-sellplans-value-cell{
          text-align:center;
          font-size:13px;
          color:#111827;
          min-width:110px;
        }

        .mk-cell-featured{
          background:rgba(255,237,213,0.35);
          border-left:1px solid rgba(249,115,22,0.18);
          border-right:1px solid rgba(249,115,22,0.18);
        }

        .mk-sellplans-check{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          width:18px;
          height:18px;
          border-radius:999px;
          border:1px solid #d1d5db;
          font-size:11px;
          font-weight:900;
        }

        .mk-dash{
          color:#9ca3af;
          font-weight:900;
        }

        .mk-sellplans-table tfoot th,
        .mk-sellplans-table tfoot td{
          padding:12px 12px 10px;
          border-top:1px solid #e5e7eb;
          background:#fff;
        }

        .mk-sellplans-cta-cell{text-align:center;}
        .mk-cta-hint{
          margin-top:6px;
          font-size:11px;
          color:#9a3412;
          font-weight:800;
        }

        .mk-sellplans-note{
          margin-top:10px;
          font-size:11px;
          color:#6b7280;
          text-align:left;
        }

        @media (max-width:768px){
          .mk-sellplans-card{padding:14px 10px;border-radius:18px;}
          .mk-sellplans-table{font-size:12px;min-width:860px;}
        }

        /* FAQ */
        .mk-faq{
          border-radius:18px;
          box-shadow:none;
        }

        .mk-faq-q{
          margin:0 0 6px;
          color:#111827;
        }

        /* Final banner */
        .mk-final-banner{
          border-radius:28px;
          background:linear-gradient(135deg, #111827 0%, #1f2937 45%, #065f46 100%);
          color:#fff;
          padding:24px;
          box-shadow:0 18px 45px rgba(15,23,42,0.20);
        }

        .mk-final-banner-inner{
          display:flex;
          justify-content:space-between;
          gap:20px;
          align-items:center;
        }

        .mk-final-eyebrow{
          margin:0 0 6px;
          color:#93c5fd;
          font-size:12px;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.14em;
        }

        .mk-final-banner h2{
          margin:0;
          font-size:28px;
          line-height:1.15;
          font-weight:900;
        }

        .mk-final-banner p{
          margin:8px 0 0;
          color:#d1d5db;
          max-width:620px;
        }

        .mk-final-actions{
          display:flex;
          flex-direction:column;
          gap:10px;
          align-items:flex-end;
        }

        @media (max-width:900px){
          .mk-final-banner-inner{
            flex-direction:column;
            align-items:flex-start;
          }
          .mk-final-actions{
            width:100%;
            align-items:flex-start;
          }
        }

        .mk-footer{
          margin-top:6px;
          font-size:12px;
          color:#9ca3af;
          text-align:center;
        }
      `}</style>
    </div>
  );
}